import { Box } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckboxRadioItem } from '@work4all/components/lib/input/checkbox-radio-item';

import { useDataMutation, useDataProvider } from '@work4all/data';

import { LookUp } from '@work4all/models/lib/Classes/LookUp.entity';
import { DataRequest } from '@work4all/models/lib/DataProvider';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { LookupType } from '@work4all/models/lib/Enums/LookupType.enum';

import { EditTableColumns } from '../../../containers/mask-overlays/mask-overlay/views/erp/components/tab-panels/positions/components/edit-table/types';
import { EditableEntityTable } from '../editable-entity-table/EditableEntityTable';

const LOOKUP_DATA: LookUp = {
  id: null,
  name: null,
  standard: null,
  closedStatus: null,
  lookupType: null,
};

const req: DataRequest = {
  data: LOOKUP_DATA,
  entity: Entities.lookUp,
  vars: {
    type: LookupType.PROJEKT_STATI,
  },
};

const newEntryMandatoryFields: (keyof LookUp)[] = ['name'];

export const ProjectStatusTable = () => {
  const { t } = useTranslation();

  const { data } = useDataProvider<LookUp>(req);

  const [mutate] = useDataMutation({
    entity: Entities.lookUp,
    mutationType: EMode.upsert,
    responseData: { id: null, standard: null },
    resetStore: false,
  });

  const handleStandardClick = useCallback(
    (id: number) => {
      for (const status of data) {
        mutate({
          id: status.id,
          standard: status.id === id ? -1 : 0,
        } as LookUp);
      }
    },
    [data, mutate]
  );

  const cols = useMemo<EditTableColumns<LookUp>[]>(
    () => [
      {
        Header: t('COMMON.NAME'),
        accessor: 'name',
        width: 300,
        type: 'text',
        resizable: false,
        editable: true,
      },
      {
        Header: t('COMMON.DEFAULT'),
        accessor: 'standard',
        width: 100,
        Cell: (e) => {
          const value = e.row.original;
          return value.id === -1 ? null : (
            <Box mt="-0.75rem">
              <CheckboxRadioItem
                control="radio"
                label=""
                checked={Boolean(value.standard)}
                onChange={() => handleStandardClick(value.id)}
                disabled={false}
              />
            </Box>
          );
        },
        resizable: false,
        editable: false,
      },
      {
        Header: t('COMMON.CLOSED'),
        accessor: 'closedStatus',
        width: 100,
        type: 'checkbox',
        editable: true,
      },
    ],
    [handleStandardClick, t]
  );

  const handleTranformBeforeUpdate = (value: LookUp): LookUp => {
    const result: LookUp = { ...value, lookupType: LookupType.PROJEKT_STATI };

    if (value.standard !== undefined) {
      result.standard = value.standard ? -1 : 0;
    }
    if (value.closedStatus !== undefined) {
      result.closedStatus = value.closedStatus ? -1 : 0;
    }
    return result;
  };

  return (
    <EditableEntityTable<LookUp>
      columns={cols}
      dataFields={LOOKUP_DATA}
      entity={Entities.lookUp}
      idType="number"
      sortByField="name"
      newEntryMandatoryFields={newEntryMandatoryFields}
      dataProviderProps={{
        vars: {
          type: LookupType.PROJEKT_STATI,
        },
      }}
      transformBeforeUpdate={handleTranformBeforeUpdate}
      enableDelete
    />
  );
};
